import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { t } from '../../../utils/translations';

const ContactInformation = ({ 
  formData, 
  onFieldChange, 
  errors = {},
  disabled = false 
}) => {
  const [contacts, setContacts] = useState(formData?.contacts || [
    { type: 'email', value: '', label: t('officeFormComponents.primaryEmail') }
  ]);

  const contactTypes = [
    { value: 'email', label: t('officeFormComponents.emailAddress'), icon: 'Mail', placeholder: 'example@ssu.org' },
    { value: 'phone', label: t('officeFormComponents.phoneNumber'), icon: 'Phone', placeholder: '+90 XXX XXX XX XX' },
    { value: 'mobile', label: t('officeFormComponents.mobilePhone'), icon: 'Smartphone', placeholder: '+90 XXX XXX XX XX' },
    { value: 'whatsapp', label: t('officeFormComponents.whatsapp'), icon: 'MessageCircle', placeholder: '+90 XXX XXX XX XX' },
    { value: 'telegram', label: t('officeFormComponents.telegram'), icon: 'Send', placeholder: '@username' },
    { value: 'linkedin', label: t('officeFormComponents.linkedin'), icon: 'Linkedin', placeholder: 'linkedin.com/in/username' },
    { value: 'twitter', label: t('officeFormComponents.twitter'), icon: 'Twitter', placeholder: '@username' },
    { value: 'website', label: t('officeFormComponents.personalWebsite'), icon: 'Globe', placeholder: 'https://website.com' }
  ];

  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = { ...updatedContacts?.[index], [field]: value };
    setContacts(updatedContacts);
    onFieldChange('contacts', updatedContacts);
  };

  const addContact = () => {
    const newContact = { type: 'email', value: '', label: '' };
    const updatedContacts = [...contacts, newContact];
    setContacts(updatedContacts);
    onFieldChange('contacts', updatedContacts);
  };

  const removeContact = (index) => {
    // Allow removing all contacts since they're no longer required
    const updatedContacts = contacts?.filter((_, i) => i !== index);
    setContacts(updatedContacts);
    onFieldChange('contacts', updatedContacts);
  };

  const validateContact = (contact) => {
    if (!contact?.value?.trim()) return null;

    switch (contact?.type) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex?.test(contact?.value) ? null : t('officeFormComponents.invalidEmailFormat');
      case 'phone': case'mobile': case'whatsapp':
        const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
        return phoneRegex?.test(contact?.value?.replace(/\s/g, '')) ? null : t('officeFormComponents.invalidPhoneFormat');
      case 'website':
        const urlRegex = /^https?:\/\/.+/;
        return urlRegex?.test(contact?.value) ? null : t('officeFormComponents.invalidUrlFormat');
      default:
        return null;
    }
  };

  const getContactTypeInfo = (type) => {
    return contactTypes?.find(ct => ct?.value === type) || contactTypes?.[0];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
          <Icon name="Contact" size={20} className="text-warning" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            {t('officeFormComponents.contactInformation')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('officeFormComponents.addMultipleContactMethods')}
          </p>
        </div>
      </div>
      {/* Contact List */}
      <div className="space-y-4">
        {contacts?.map((contact, index) => {
          const contactInfo = getContactTypeInfo(contact?.type);
          const validationError = validateContact(contact);
          
          return (
            <div key={index} className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-start space-x-4">
                {/* Contact Type Selector */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name={contactInfo?.icon} size={18} className="text-muted-foreground" />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Contact Type */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">
                        {t('officeFormComponents.contactType')}
                      </label>
                      <select
                        value={contact?.type}
                        onChange={(e) => handleContactChange(index, 'type', e?.target?.value)}
                        disabled={disabled}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        {contactTypes?.map((type) => (
                          <option key={type?.value} value={type?.value}>
                            {type?.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Contact Label */}
                    <Input
                      label={t('officeFormComponents.labelOptional')}
                      type="text"
                      placeholder={t('officeFormComponents.labelPlaceholder')}
                      value={contact?.label}
                      onChange={(e) => handleContactChange(index, 'label', e?.target?.value)}
                      disabled={disabled}
                      className="text-sm"
                    />
                  </div>

                  {/* Contact Value */}
                  <Input
                    label={contactInfo?.label}
                    type={contact?.type === 'email' ? 'email' : 'text'}
                    placeholder={contactInfo?.placeholder}
                    value={contact?.value}
                    onChange={(e) => handleContactChange(index, 'value', e?.target?.value)}
                    error={validationError}
                    disabled={disabled}
                  />
                </div>

                {/* Remove Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0 text-muted-foreground hover:text-error"
                  onClick={() => removeContact(index)}
                  disabled={disabled}
                  iconName="Trash2"
                  iconSize={16}
                  title={t('officeFormComponents.removeContact')}
                />
              </div>
            </div>
          );
        })}
      </div>
      {/* Add Contact Button */}
      <Button
        variant="outline"
        onClick={addContact}
        disabled={disabled || contacts?.length >= 8}
        iconName="Plus"
        iconPosition="left"
        iconSize={16}
        className="w-full"
      >
        {t('officeFormComponents.addAnotherContactMethod')}
      </Button>
      {/* Contact Guidelines */}
      <div className="p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-2">{t('officeFormComponents.contactGuidelines')}</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>{t('officeFormComponents.contactGuideline1')}</li>
              <li>{t('officeFormComponents.contactGuideline2')}</li>
              <li>{t('officeFormComponents.contactGuideline3')}</li>
              <li>{t('officeFormComponents.contactGuideline4')}</li>
              <li>{t('officeFormComponents.contactGuideline5')}</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Contact Summary */}
      {contacts?.filter(c => c?.value?.trim())?.length > 0 && (
        <div className="p-4 bg-accent/5 rounded-lg border border-accent/20">
          <h4 className="text-sm font-medium text-foreground mb-3">{t('officeFormComponents.contactSummary')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {contacts?.filter(contact => contact?.value?.trim())?.map((contact, index) => {
                const contactInfo = getContactTypeInfo(contact?.type);
                const isValid = !validateContact(contact);
                
                return (
                  <div
                    key={index}
                    className={`flex items-center space-x-2 text-xs p-2 rounded ${
                      isValid ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                    }`}
                  >
                    <Icon name={contactInfo?.icon} size={14} />
                    <span className="font-medium">
                      {contact?.label || contactInfo?.label}:
                    </span>
                    <span className="truncate">{contact?.value}</span>
                    <Icon name={isValid ? 'CheckCircle' : 'AlertCircle'} size={12} />
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactInformation;